<script lang="ts">
	import { beforeUpdate, onDestroy, onMount } from 'svelte';
	import { format } from '../../../services/i18n';

	import {
		Breadcrumbs,
		CardGrid,
		IconButton,
		Input,
		Toolbar,
	} from '@Shelly/shelly-design-system';
	import { temp_schemes } from 'src/stores/lists/scenes-derived';
	import { edit } from 'src/stores/ui/edit_lists';
	import { devices } from 'src/stores/lists/devices';
	import { requestReorderDevice } from '../list-helpers';

	import ThermostatCard from './ThermostatCard.svelte';
	import EmptyList from '../EmptyList.svelte';
	import ListEmptyStateSurface from '../ListEmptyStateSurface.svelte';
	import {
		resetThermostatsMainFilter,
		thermostatsMainFilter,
		thermostatsFilter,
		getSelectedThermostatFilterCount,
	} from 'src/stores/ui/filters';
	import { defaultThermostatFilter } from '../../../helpers/constants';
	import ThermostatWizard from '../../TemperatureScheme/TempSchemeWizard/ThermostatWizard.svelte';
	import { _deepClone, capFirstChar } from '../../../helpers/utilities';
	import { temp_scheme_prototype } from '../../TemperatureScheme/TempSchemeWizard/temp_scheme_object';
	import type { ITempSchemeObj } from '../../../types/TemperatureSchemeType';
	import { defaultSelectedRoom } from 'src/helpers/constants';
	import { hasMissingThermostatDevice } from 'src/components/Scenes/helpers/has_missing_thermostat_device';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import RoomToolbarWrapper from '../Rooms/RoomToolbarWrapper.svelte';
	import ThermostatDeleteModal from 'src/components/Drawer/ThermostatDrawer/ThermostatDeleteModal.svelte';
	import ThermostatsToolbar from './ThermostatsToolbar.svelte';
	import RoomBreadCrumbs from '../Rooms/RoomBreadCrumbs.svelte';
	import RoomToolbar from '../Rooms/RoomToolbar.svelte';
	import AddEditButtons from 'src/components/AddEditButtons.svelte';
	import ThermostatsFilterModal from './ThermostatsFilterModal.svelte';
	import { ComponentType } from '@Shelly/shelly-design-system/helpers/enums';
	import { clearThermostatsItemNewFlag } from 'src/helpers/add-entity-helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';
	import {
		getSharedHomeEmptyListCopy,
		type SharedHomeManagedContext,
	} from 'src/helpers/houses/shared_home_messaging_helpers';

	export let list;
	export let type = 'thermostats';
	export let id;

	let temp_scheme_object: ITempSchemeObj = _deepClone(temp_scheme_prototype) as ITempSchemeObj;
	let thermostat_id,
		thermostat_name = '';

	let openFilterModal = false;

	const requestType = 'scene';
	const flipDurationMs = 0;

	let showAddEditModal = false;
	let showSharedManagedModal = false;
	let sharedManagedContext: SharedHomeManagedContext = 'add_thermostat';
	let showDeleteThermostatModal = false;
	let dragDisabled = true;
	let currentRoute = '';

	$: filteredList = (() => {
		const selectedRoom = $thermostatsFilter.rooms[0].value;
		const selectedType = $thermostatsFilter.type[0].value;
		return [...list]
			.filter((el) => selectedRoom === defaultSelectedRoom.value || el._meta.room === selectedRoom)
			.filter(
				(el) => selectedType === defaultThermostatFilter.value || el._meta.type === selectedType,
			);
	})();
	$: isEmptyDeviceList = Object.values($devices).length === 0;
	$: items = $edit && currentRoute === type ? items : filteredList;
	$: dndSettings = {
		items,
		flipDurationMs: flipDurationMs,
		dragDisabled: dragDisabled,
	};

	$: notification =
		getSelectedThermostatFilterCount($thermostatsFilter) > 0
			? ({
					title: getSelectedThermostatFilterCount($thermostatsFilter),
					type: ComponentType.DANGER,
				} as const)
			: null;

	onMount(async () => {
		currentRoute = type;
	});

	onDestroy(() => {
		edit.set(false);
		resetThermostatsMainFilter();
		clearThermostatsItemNewFlag(id);
	});

	beforeUpdate(async () => {
		if (!$edit) handleEndDrag();

		if (currentRoute !== type) {
			currentRoute = type;
		}
	});

	const onCardClick = (item) => {
		if ($edit) return;

		setSidePanelState({
			type: 'thermostat',
			id: item._id,
			open: true,
		});
	};

	const onCardEdit = async (event: CustomEvent, item) => {
		event.stopPropagation();
		showAddEditModal = true;
		thermostat_id = item._id;

		thermostat_name = item._meta.name;
		temp_scheme_object = _deepClone($temp_schemes[thermostat_id]) as ITempSchemeObj;
	};

	const onCardDelete = (event: CustomEvent, item) => {
		event.stopPropagation();
		thermostat_id = item._id;
		thermostat_name = item._meta.name;
		showDeleteThermostatModal = true;
	};

	// DND Functions
	const handleStartDrag = () => {
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		dragDisabled = true;
	};

	const handleReorder = (e: CustomEvent) => {
		items = e.detail.items;
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		let editedList = {};
		e.detail.items.map((v, i) => {
			delete v?.isDndShadowItem;
			v.id = String(v.id);
			v._id = String(v._id);
			v._name = String(v._id);

			editedList[v.id] = {
				...v,
				_meta: {
					...v._meta,
					position: i,
				},
			};
		});

		if (Object.keys(editedList).length === 0) return;

		items = [...e.detail.items];
		handleEndDrag();

		await requestReorderDevice(requestType, editedList);
	};

	const handle_add_thermostat = () => {
		if (isActiveHomeShared($homes.list)) {
			sharedManagedContext = 'add_thermostat';
			showSharedManagedModal = true;
			return;
		}
		edit.set(false);
		thermostat_id = '';
		temp_scheme_object = _deepClone(temp_scheme_prototype) as ITempSchemeObj;
		if (type === 'room') {
			temp_scheme_object._meta.room = +id;
		}
		showAddEditModal = true;
	};
</script>

{#if $showNewLayout && type === 'room'}
	<RoomToolbarWrapper
		showBulkActions={false}
		{id}
		{type}
		{list}
		{currentRoute}
		itemsLength={items.length}
	/>
{:else if $showNewLayout}
	<ThermostatsToolbar disableReorder={items.length <= 1} {type} {id} />
{:else}
	<Toolbar class="mb-m" isSticky>
		<svelte:fragment slot="breadcrumbs">
			{#if type === 'room'}
				<RoomBreadCrumbs {id} />
			{:else}
				<Breadcrumbs
					aria-label={$format('label_thermostats_data_card', { default: 'Thermostats' })}
					links={[{ label: capFirstChar($format('label_thermostats_data_card')) }]}
				/>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="leftSide">
			{#if type === 'room'}
				<RoomToolbar />
			{/if}

			{#if type === 'thermostats'}
				<div class="d-flex align-items-center justify-content-center">
					<Input
						class="width-md-m mr-sm-s text-capitalize-placeholder"
						bind:value={$thermostatsMainFilter}
						type="text"
						label={capFirstChar($format('search', { default: 'Search' }))}
						icon="fas fa-search"
						id="thermostats-toolbar-search-input"
						aria-labelledby="thermostats-toolbar-search-input"
					/>
					<IconButton
						icon="fa-solid fa-filter"
						on:click={() => {
							openFilterModal = true;
						}}
						aria-label={$format('aria_open_filter', { default: 'Open filter' })}
						{notification}
					/>
				</div>
			{/if}
		</svelte:fragment>

		<svelte:fragment slot="rightSide">
			<AddEditButtons
				disableEditButton={items.length <= 0}
				disableAddButton={isEmptyDeviceList}
				hideAddButton={$showNewLayout}
				interceptEditClick={isActiveHomeShared($homes.list)}
				on:add_click={() => handle_add_thermostat()}
				on:edit_click={() => {
					sharedManagedContext = 'edit_entity';
					showSharedManagedModal = true;
				}}
			/>
		</svelte:fragment>
	</Toolbar>
{/if}

{#if items.length <= 0 && !!$thermostatsMainFilter}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-magnifying-glass"
			title={$format('no_such_thermostats_found', { default: 'No such thermostats found.' })}
			description={$format('check_the_spelling_or_try_new_search', {
				default: 'Check the spelling or try a new search.',
			})}
		/>
	</ListEmptyStateSurface>
{:else if items.length <= 0 && isActiveHomeShared($homes.list)}
	{@const sharedEmptyCopy = getSharedHomeEmptyListCopy('thermostats')}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-temperature-three-quarters"
			title={$format(sharedEmptyCopy.titleKey, { default: sharedEmptyCopy.titleDefault })}
			description={$format(sharedEmptyCopy.descrKey, {
				default: sharedEmptyCopy.descrDefault,
			})}
		/>
	</ListEmptyStateSurface>
{:else if items.length <= 0}
	<ListEmptyStateSurface class="mt-md-l mt-sm-s">
		<EmptyList
			icon="fal fa-temperature-three-quarters"
			title={$format('no_thermostats_title', {
				default: 'There are no thermostats yet',
			})}
			description={$format('no_thermostat_descr', {
				default: 'Try creating a Thermostat for easier and/or scheduled temperature control.',
			})}
			btnText={$format('button_add_thermostat', {
				default: 'Add thermostat',
			})}
			onClick={() => handle_add_thermostat()}
		/>
	</ListEmptyStateSurface>
{:else}
	<CardGrid
		class={$edit && $showNewLayout ? 'mt-s' : ''}
		editMode={$edit}
		xl={350}
		lg={290}
		sm={270}
		{dndSettings}
		on:consider={handleReorder}
		on:finalize={handleReorderEnd}
	>
		{#each items as item, _ (item.id + '-' + $edit)}
			{@const missingDevice = hasMissingThermostatDevice(item.id)}
			<div
				class:dnd-placeholder={item.isDndShadowItem}
				tabindex="0"
				role="button"
				aria-label={$format('button_drag_thermostat_to_reorder', {
					default: 'Drag thermostat to reorder',
				})}
			>
				<ThermostatCard
					{item}
					{missingDevice}
					on:click={() => onCardClick(item)}
					on:handle-drag-start={handleStartDrag}
					on:handle-drag-end={handleEndDrag}
					on:edit={(event) => onCardEdit(event, item)}
					on:delete={(event) => onCardDelete(event, item)}
				/>
			</div>
		{/each}
	</CardGrid>
{/if}

{#if showAddEditModal}
	<ThermostatWizard bind:id={thermostat_id} bind:showAddEditModal {temp_scheme_object} />
{/if}

<ThermostatDeleteModal bind:showDeleteThermostatModal bind:items {thermostat_id} {thermostat_name} />

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context={sharedManagedContext} />

{#if openFilterModal}
	<ThermostatsFilterModal bind:openFilterModal />
{/if}
